import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { BillingCycleStatusBadge } from "@/components/billing-cycles/BillingCycleStatusBadge";
import { HowThisWorks } from "@/components/billing-cycles";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { APIError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import {
  useResourceDetail,
  useUpdateResourceExpiry,
} from "@/hooks/useResourceQueries";
import {
  useBillingCycles,
  useUpdateBillingCycle,
} from "@/hooks/useBillingCycleQueries";
import {
  resourceExpirySchema,
  type ResourceExpiryFormData,
} from "@/schemas/adminResourceExpiry.schema";
import {
  billingCycleUpdateSchema,
  type BillingCycleUpdateFormData,
} from "@/schemas/adminBillingCycle.schema";
import { BILLING_CYCLE_STATUSES } from "@/types/billing-cycle";

/**
 * Operator screen for a machine's term: the resource's own expiry on the left, the billing cycle
 * that records the paid period on the right.
 *
 * The two are the same date held in two tables, and the backend keeps them in step from one side
 * only: saving the expiry moves the active cycle's end with it
 * (`UpdateResourceExpiryCommandHandler` calls `activeCycle.OverrideWindow(...)`), which is exactly
 * what the banner between the panels explains. Saving the cycle panel on its own does not reach
 * back the other way — correcting the cycle's own dates or status here leaves the resource's expiry
 * untouched, so the banner can still show the pair has drifted after that save until someone
 * reconciles it.
 *
 * Reached from the resource list, the resource detail page, and the billing-cycle list. That last
 * one names a specific cycle in `?cycleId=`, because the row an operator clicked may be a closed
 * historical cycle rather than the machine's current one.
 */
export function ResourceExpiryManagementPage() {
  const { id: resourceId = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const requestedCycleId = searchParams.get("cycleId");

  const {
    data: resource,
    isLoading: resourceLoading,
    isFetching: resourceFetching,
    isError: resourceFailed,
    error: resourceError,
    refetch: refetchResource,
  } = useResourceDetail(resourceId);

  // Every cycle this resource has ever had. The list endpoint is the only way in: the admin
  // resource DTO carries no billingCycleId, so there is nothing to look one up by.
  const {
    data: cyclePage,
    isLoading: cyclesLoading,
    isFetching: cyclesFetching,
    isError: cyclesFailed,
    refetch: refetchCycles,
  } = useBillingCycles({
    resourceId,
    page: 1,
    pageSize: 50,
    sortBy: "CycleEnd",
    sortOrder: "desc",
  });

  const cycles = useMemo(() => cyclePage?.data ?? [], [cyclePage]);

  // The cycle named in the URL, else the live one, else the most recent. A closed cycle is a
  // legitimate target: correcting the record of a period that has already ended is half of why
  // this screen exists.
  const cycle = useMemo(() => {
    if (requestedCycleId) {
      return cycles.find((c) => c.id === requestedCycleId) ?? null;
    }

    return cycles.find((c) => c.status === "Active") ?? cycles[0] ?? null;
  }, [cycles, requestedCycleId]);

  const updateExpiry = useUpdateResourceExpiry();
  const updateCycle = useUpdateBillingCycle();

  const expiryForm = useForm<ResourceExpiryFormData>({
    resolver: zodResolver(resourceExpirySchema),
    defaultValues: { expiresAt: "", reason: "" },
  });

  const cycleForm = useForm<BillingCycleUpdateFormData>({
    resolver: zodResolver(billingCycleUpdateSchema),
    defaultValues: { cycleStart: "", cycleEnd: "", status: "Active" },
  });

  const expiryControl = expiryForm.control as unknown as Control<ResourceExpiryFormData>;
  const cycleControl = cycleForm.control as unknown as Control<BillingCycleUpdateFormData>;

  // Seeded once the row arrives, and re-seeded on every fresh read after that — including the
  // billing-cycle refetch that follows a successful expiry save, since (per the header comment
  // above) saving the expiry moves the active cycle's `cycleEnd` server-side. Skipping the re-seed
  // outright while a form was dirty (the previous shape of this effect) broke exactly that case: an
  // operator who left the cycle Status edited-but-unsaved would have the refetched `cycleEnd`
  // silently discarded, and their next "Save billing cycle" would then submit the OLD end date and
  // undo the expiry save that just landed.
  //
  // `keepDirtyValues` is what makes an unconditional re-seed safe: a field the operator has
  // actually edited keeps its typed value (and stays flagged dirty) through the reset, while every
  // field they have not touched picks up whatever the server now says. That also covers the other
  // source of an incoming refetch this page doesn't control — another admin editing the same row, a
  // window-focus refetch, `staleTime` expiring — without throwing away an edit still in progress.
  // The reason box is deliberately not part of either reset payload's memory: it belongs to the
  // edit being made, not to the row.
  useEffect(() => {
    if (resource) {
      expiryForm.reset(
        { expiresAt: resource.expiresAt ?? "", reason: "" },
        { keepDirtyValues: true },
      );
    }
  }, [resource, expiryForm]);

  // One narrow window is left open, knowingly. The expiry save waits for this cycle refetch only up
  // to the `awaitRefresh` cap (8s). If the refetch is slower than that, the forms unlock while this
  // form still shows the old `cycleEnd`, and a "Save billing cycle" pressed before the refetch lands
  // would send it. The drift banner is up at that moment — the two dates disagree on screen — and the
  // re-seed below corrects the field as soon as the refetch arrives.
  useEffect(() => {
    if (cycle) {
      cycleForm.reset(
        { cycleStart: cycle.cycleStart, cycleEnd: cycle.cycleEnd, status: cycle.status },
        { keepDirtyValues: true },
      );
    }
  }, [cycle, cycleForm]);

  const pickedExpiry = expiryForm.watch("expiresAt");
  const pickedCycleEnd = cycleForm.watch("cycleEnd");
  const pickedStatus = cycleForm.watch("status");

  const pickedExpiryDate = pickedExpiry ? new Date(pickedExpiry) : null;
  const expiryIsInThePast =
    pickedExpiryDate !== null &&
    !Number.isNaN(pickedExpiryDate.getTime()) &&
    pickedExpiryDate.getTime() <= Date.now();

  // The whole point of putting the two panels side by side: say out loud when they disagree.
  // Compared on the instant, not the string — the two columns are written by different code paths
  // and an identical moment can arrive with a different ISO representation.
  const datesDrifted = useMemo(() => {
    if (!pickedExpiry || !pickedCycleEnd) return false;

    const left = new Date(pickedExpiry).getTime();
    const right = new Date(pickedCycleEnd).getTime();

    return (
      !Number.isNaN(left) && !Number.isNaN(right) && left !== right
    );
  }, [pickedExpiry, pickedCycleEnd]);

  const activatingAnInactiveCycle =
    pickedStatus === "Active" && cycle !== null && cycle.status !== "Active";

  // Both writes lock both forms (see `anyPending` below), so the two saves can never interleave.
  const anyPending = updateExpiry.isPending || updateCycle.isPending;

  const handleSaveExpiry = ({ expiresAt, reason }: ResourceExpiryFormData) => {
    updateExpiry.mutate(
      { id: resourceId, expiresAt, reason: reason?.trim() ? reason.trim() : undefined },
      {
        // A plain reset — no `keepDirtyValues` — right after the save the operator just made. The
        // re-seed effect's own reset above never clears `dirtyFields` on its own (that is exactly
        // what `keepDirtyValues` preserves), so without this call the field would read as "still
        // edited" forever even once its value matches what was just saved, and a later refetch this
        // page doesn't control would keep refusing to update it. The reason box is cleared here too
        // — it is a one-off note for this edit, already recorded in the audit trail, not a value
        // that belongs to the row.
        onSuccess: () => expiryForm.reset({ expiresAt, reason: "" }),
      },
    );
  };

  const handleSaveCycle = (data: BillingCycleUpdateFormData) => {
    if (!cycle) return;

    updateCycle.mutate(
      { id: cycle.id, payload: data },
      // Same reasoning as `handleSaveExpiry` above: clears the dirty flags this save just earned,
      // so the next `keepDirtyValues` re-seed treats these fields as settled rather than as an edit
      // still in progress.
      { onSuccess: () => cycleForm.reset(data) },
    );
  };

  if (resourceLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  // `resourceFailed` alone also fires when a BACKGROUND refetch fails with the previous data still
  // in the cache — including the refetch this page's own "Save expiry" awaits (see `onSettled` on
  // `useUpdateResourceExpiry`). Replacing the whole page — both forms, the just-succeeded save's
  // own overlay — with "could not be loaded" on that transient failure would be wrong on its own
  // successful save. The full-screen state is only for when there is truly nothing to show.
  if (resourceFailed && !resource) {
    // A confirmed 404 means the row is genuinely gone — retrying only asks the same question
    // again. Anything else (network blip, 500) is worth a retry, and that retry belongs before
    // the way out: one failed request should not cost the operator a trip back to the list to
    // find this same row again.
    const notFound =
      resourceError instanceof APIError && resourceError.statusCode === 404;

    return (
      <div className="space-y-4 p-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="space-y-3">
            <p>
              {notFound
                ? "This resource could not be found. It may have been deleted."
                : "This resource could not be loaded."}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {!notFound && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={resourceFetching}
                  onClick={() => refetchResource()}
                  className="cursor-pointer"
                >
                  {resourceFetching && <Spinner className="mr-2" />}
                  Retry
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="cursor-pointer"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!resource) {
    // Reachable only for type-narrowing below: once `resourceLoading` is false and the query
    // hasn't failed outright (handled above), a successful read always carries `data`.
    return null;
  }

  // A refetch failed but the last successful read is still on screen (see the comment above): say
  // so above the forms instead of hiding them behind the full-screen state.
  const resourceStale = resourceFailed;

  return (
    <div className="space-y-4 p-4">
      {resourceStale && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center gap-2">
            <span>
              This resource could not be refreshed. What is shown below may be out of date.
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={resourceFetching}
              onClick={() => refetchResource()}
              className="cursor-pointer"
            >
              {resourceFetching && <Spinner className="mr-2" />}
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold">{resource.name}</h1>
          <p className="text-sm text-muted-foreground">
            Saving the expiry moves the billing period with it. The billing
            period can also be edited on its own.
          </p>
        </div>

        {/* Right corner: both leave this screen, so they sit away from the two Save buttons rather
            than beside the title where they read as part of the heading. */}
        <div className="flex shrink-0 items-center gap-2">
          <HowThisWorks anchor="expiry-screen" />

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="shrink-0 cursor-pointer"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      {datesDrifted && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            The expiry date and the cycle end date are not the same. They are meant to be one
            date: the suspension scan and the renewal quote read the resource&apos;s expiry, while
            the expiry scan and every billing report read the cycle. Saving the expiry on the left
            brings the cycle with it; the nightly reconciliation would otherwise do the same at
            03:30 UTC.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ---- Left: the resource's own expiry ---- */}
        <Card>
          <CardHeader>
            <CardTitle>Resource expiry</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted-foreground">Status</dt>
              <dd>{resource.status}</dd>
              <dt className="text-muted-foreground">Current expiry</dt>
              <dd>
                {resource.expiresAt
                  ? formatDateTime(resource.expiresAt)
                  : "No expiry set"}
              </dd>
            </dl>

            <Form {...expiryForm}>
              <form
                onSubmit={expiryForm.handleSubmit(handleSaveExpiry)}
                className="space-y-4"
              >
                <FormField
                  control={expiryControl}
                  name="expiresAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New expiry date</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? "")}
                          includeTime
                          placeholder="Select expiry date"
                          disabled={anyPending}
                        />
                      </FormControl>
                      <FormDescription>
                        Shown in your local time, stored in UTC.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={expiryControl}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason / description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          maxLength={500}
                          placeholder="Why is this being changed? (optional)"
                          disabled={anyPending}
                        />
                      </FormControl>
                      <FormDescription>
                        Optional. Recorded in the audit trail only — no field on the resource holds
                        it.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {expiryIsInThePast && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      This date is in the past. The expiry scan will suspend the VM on its next run
                      — not immediately.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end">
                  <Button type="submit" disabled={anyPending}>
                    {updateExpiry.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save expiry
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* ---- Right: the billing cycle ---- */}
        <Card>
          <CardHeader>
            <CardTitle>Billing cycle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* A refetch failed but a previous page is still cached — including the refetch this
                page's own "Save expiry" awaits (that mutation invalidates billing cycles too, see
                the header comment). Reporting it inline, above whatever the cached page still
                shows, keeps the cycle form usable instead of replacing it with a full "could not be
                loaded" state right after a save that just succeeded. */}
            {cyclesFailed && cyclePage && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="flex flex-wrap items-center gap-2">
                  <span>
                    The billing cycle could not be refreshed. What is shown below may be out of
                    date.
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cyclesFetching}
                    onClick={() => refetchCycles()}
                    className="cursor-pointer"
                  >
                    {cyclesFetching && <Spinner className="mr-2" />}
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {cyclesLoading ? (
              <Skeleton className="h-64" />
            ) : cyclesFailed && !cyclePage ? (
              // A failed read must never be presented as a fact — showing "no billing cycle" here
              // would tell the operator a resource has none when the truth is simply that the
              // request didn't come back. This is the never-loaded case only; a failed refetch with
              // a cached page is reported by the inline banner above instead.
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p>The billing cycle could not be loaded.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cyclesFetching}
                    onClick={() => refetchCycles()}
                    className="cursor-pointer"
                  >
                    {cyclesFetching && <Spinner className="mr-2" />}
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            ) : !cycle ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This resource has no billing cycle, so there is nothing to correct here. Creating
                  one is a billing decision this screen does not make.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-muted-foreground">Current status</dt>
                  <dd>
                    <BillingCycleStatusBadge status={cycle.status} />
                  </dd>
                  <dt className="text-muted-foreground">Amount</dt>
                  <dd>{formatCurrency(cycle.amount, cycle.currency)}</dd>
                  <dt className="text-muted-foreground">Current term</dt>
                  <dd>
                    {formatDateTime(cycle.cycleStart)} —{" "}
                    {formatDateTime(cycle.cycleEnd)}
                  </dd>
                </dl>

                <Form {...cycleForm}>
                  <form
                    onSubmit={cycleForm.handleSubmit(handleSaveCycle)}
                    className="space-y-4"
                  >
                    <FormField
                      control={cycleControl}
                      name="cycleStart"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start date</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? "")}
                              includeTime
                              placeholder="Select start date"
                              disabled={anyPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={cycleControl}
                      name="cycleEnd"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End date</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? "")}
                              includeTime
                              placeholder="Select end date"
                              disabled={anyPending}
                            />
                          </FormControl>
                          <FormDescription>
                            Must be after the start date.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={cycleControl}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={anyPending}
                          >
                            <FormControl>
                              <SelectTrigger className="cursor-pointer">
                                <SelectValue placeholder="Select a status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {BILLING_CYCLE_STATUSES.map((status) => (
                                <SelectItem
                                  key={status}
                                  value={status}
                                  className="cursor-pointer"
                                >
                                  {status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {activatingAnInactiveCycle && (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          A resource may have only one active billing cycle. If another one is
                          already active, saving will be refused — move that one out of Active
                          first.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="flex justify-end">
                      <Button type="submit" disabled={anyPending}>
                        {updateCycle.isPending && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Save billing cycle
                      </Button>
                    </div>
                  </form>
                </Form>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* The page banner above already says saving the expiry moves the cycle with it, so the two
          writes must never interleave — both forms are locked (see `anyPending`) and this overlay
          covers the whole page for whichever one is actually running. */}
      <BusyOverlay
        open={anyPending}
        label={updateExpiry.isPending ? "Saving expiry…" : "Saving billing cycle…"}
      />
    </div>
  );
}

export default ResourceExpiryManagementPage;
